import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { SearchInput } from '@/components/SearchInput';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { api } from '@/lib/api';
import { courseTotalActiveSeconds } from '@/lib/course-duration';
import { LearningSessionTimeTable, type LearningSessionTimeRow } from './LearningSessionTimeTable';

interface CourseSessionTimeData {
  course: { id: string; name: string };
  data: LearningSessionTimeRow[];
  meta: { total: number; page: number; perPage: number; lastPage: number };
}

export function CourseSessionTimePage() {
  const { id: courseId = '' } = useParams<{ id: string }>();
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch();
  const appliedSearch = debounced.trim();

  const { data: courseMeta } = useQuery({
    queryKey: ['courses', courseId],
    queryFn: () =>
      api<{
        name: string;
        category?: { id: string };
        academicHours?: number | string | null;
        minCompliancePercent?: number | string | null;
      }>(`/courses/${courseId}`),
    enabled: Boolean(courseId),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-session-course-time', courseId, appliedSearch, page],
    queryFn: () =>
      api<CourseSessionTimeData>(
        `/admin/learning-sessions/course-time/${courseId}?page=${page}${
          appliedSearch ? `&search=${encodeURIComponent(appliedSearch)}` : ''
        }`,
      ),
    enabled: Boolean(courseId),
  });

  const records = data?.data ?? [];
  const meta = data?.meta;
  const perPage = meta?.perPage ?? 30;
  const total = meta?.total ?? records.length;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);
  const hasSearch = search.trim().length > 0;

  function clearFilters() {
    setSearch('');
  }

  const courseTotalSeconds = courseTotalActiveSeconds(courseMeta?.academicHours);

  if (!courseId) return null;

  return (
    <CourseHubShell
      courseId={courseId}
      courseName={data?.course.name ?? courseMeta?.name ?? 'Curso'}
      categoryId={courseMeta?.category?.id}
      activeTab="sesion"
      description="Consulta el tiempo de conexión activo de los estudiantes matriculados en este curso."
    >
      {error ? (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          <div className="lms-course-forum-card__head">
            <div className="lms-course-forum-card__heading">
              <h2 className="lms-course-forum-card__title">Horas de sesión</h2>
            </div>

            <form className="lms-course-forum-card__tools" onSubmit={(event) => event.preventDefault()}>
              <SearchInput
                id="course-session-time-search"
                name="search"
                value={search}
                onChange={setSearch}
                placeholder="Buscar estudiantes..."
                aria-label="Buscar estudiantes"
                className="lms-course-forum-card__search"
              />
              {hasSearch ? (
                <button type="button" className="lms-gradebook__clear" onClick={clearFilters}>
                  Limpiar
                </button>
              ) : null}
            </form>
          </div>

          <LearningSessionTimeTable
            embedded
            records={records}
            isLoading={isLoading}
            page={page}
            lastPage={lastPage}
            total={total}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onPageChange={setPage}
            courseTimeLabel="Tiempo en el curso"
            courseTotalActiveSeconds={courseTotalSeconds}
            academicHours={courseMeta?.academicHours}
            minCompliancePercent={courseMeta?.minCompliancePercent}
            emptyMessage="No hay estudiantes con tiempo de sesión registrado en este curso."
          />
        </div>
      </div>
    </CourseHubShell>
  );
}
